import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatsCard } from '../components/StatsCard';
import { AdminSearchBar } from '../components/AdminSearchBar';
import { AdminProviderTable } from '../components/AdminProviderTable';
import { QuickActionCard } from '../components/QuickActionCard';
import { getAdminProvidersList, getAdminDashboardSummary } from '../services/api';
import { AdminProviderListItem, DashboardSummary, ProviderVerificationStatus } from '../types';
import { ROUTES } from '../constants';
import { Users, CheckCircle, Clock, ShieldOff, Flag, MessageSquare, Calendar, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allProviders, setAllProviders] = useState<AdminProviderListItem[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<AdminProviderListItem[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<ProviderVerificationStatus | 'all'>('all');

  const fetchProviders = async () => {
    setProvidersLoading(true);
    const response = await getAdminProvidersList({});
    if (response.success) {
      setAllProviders(response.providers);
    } else {
      alert(response.error || 'Failed to fetch providers');
    }
    setProvidersLoading(false);
  };

  const fetchSummary = async () => {
    const response = await getAdminDashboardSummary();
    if (response.success && response.data) {
      setSummary(response.data);
    }
  };

  const filterProviders = () => {
    let filtered = [...allProviders];

    if (activeTab === 'pending') {
      filtered = filtered.filter((provider) => !provider.isVerified && !provider.isBlocked);
    } else if (activeTab === 'verified') {
      filtered = filtered.filter((provider) => provider.isVerified && !provider.isBlocked);
    } else if (activeTab === 'blocked') {
      filtered = filtered.filter((provider) => provider.isBlocked);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (provider) =>
          provider.name.toLowerCase().includes(query) ||
          provider.email.toLowerCase().includes(query)
      );
    }

    setFilteredProviders(filtered);
  };

  useEffect(() => {
    fetchProviders();
    fetchSummary();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [allProviders, activeTab, searchQuery]);

  if (user?.role !== 'admin') {
    return <Navigate to={ROUTES.HOME_PAGE} replace />;
  }

  const handleViewDetails = (providerId: number) => {
    navigate(`${ROUTES.ADMIN_PROVIDER_DETAILS}?id=${providerId}`);
  };

  const tabs: { id: ProviderVerificationStatus | 'all'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'verified', label: 'Verified' },
    { id: 'blocked', label: 'Blocked' },
  ];

  const totalBookings = summary?.bookings.total ?? 0;
  const pendingBookings = summary?.bookings.pending ?? 0;
  const flaggedReviews = summary?.reviews.flagged ?? 0;
  const pendingDeleteRequests = summary?.deleteRequests.pending ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header theme="blue" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage provider verifications and accounts</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Providers"
            value={summary?.providers.total ?? 0}
            icon={Users}
            color="slate"
          />
          <StatsCard
            title="Verified"
            value={summary?.providers.verified ?? 0}
            icon={CheckCircle}
            color="green"
          />
          <StatsCard
            title="Pending Verification"
            value={summary?.providers.pending ?? 0}
            icon={Clock}
            color="yellow"
          />
          <StatsCard
            title="Blocked"
            value={summary?.providers.blocked ?? 0}
            icon={ShieldOff}
            color="red"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Bookings"
            value={summary?.bookings.total ?? 0}
            icon={Calendar}
            color="slate"
          />
          <StatsCard
            title="Pending Bookings"
            value={summary?.bookings.pending ?? 0}
            icon={Clock}
            color="yellow"
          />
          <StatsCard
            title="Completed Bookings"
            value={summary?.bookings.completed ?? 0}
            icon={CheckCircle}
            color="green"
          />
          <StatsCard
            title="Flagged Reviews"
            value={summary?.reviews.flagged ?? 0}
            icon={Flag}
            color="yellow"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <QuickActionCard
            title="Manage All Bookings"
            description={`${totalBookings} total booking${totalBookings !== 1 ? 's' : ''}${pendingBookings > 0 ? ` • ${pendingBookings} pending` : ''}`}
            icon={Calendar}
            onClick={() => navigate(ROUTES.ADMIN_BOOKINGS)}
          />
          <QuickActionCard
            title="Manage All Reviews"
            description={`View and moderate all reviews${flaggedReviews > 0 ? ` • ${flaggedReviews} flagged` : ''}`}
            icon={MessageSquare}
            onClick={() => navigate(ROUTES.ADMIN_REVIEWS)}
          />
          <QuickActionCard
            title="Account Deletion Requests"
            description={`${pendingDeleteRequests} pending deletion request${pendingDeleteRequests !== 1 ? 's' : ''}`}
            icon={Trash2}
            onClick={() => navigate(ROUTES.ADMIN_DELETE_REQUESTS)}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex gap-2 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-slate-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="w-full md:w-96">
                <AdminSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by name or email..."
                />
              </div>
            </div>
          </div>

          <AdminProviderTable
            providers={filteredProviders}
            loading={providersLoading}
            onViewDetails={handleViewDetails}
          />
        </div>
      </div>
    </div>
  );
};
